import React, { useState, useEffect, useRef } from 'react'
import { AdyenCheckout, ApplePay } from '@adyen/adyen-web'
import { ShopperBasketsV2 } from 'commerce-sdk-isomorphic'
import { useAccessToken, useShopperOrdersMutation, ShopperOrdersMutations } from '@salesforce/commerce-sdk-react'
import { useCurrentBasket } from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import { useCurrentCustomer } from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import { getConfig } from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import { getSiteEnvVarValue, getBrandSiteEnvVarValue, getBrandLocaleEnvVarValue } from '../../../utils/site-config'
import { useIntl } from 'react-intl'
import {useBrand} from '../../../contexts/brand-context'
import { sendClickEvent, sendBeginCheckoutEvent } from '../../../utils/gtm-utils'
import usedDeviceDetection from '../../../hooks/use-device-detection'
//fetch SFCC payment methods and filter out & return the Apple Pay payment method
const getApplePayPaymentMethod = (paymentMethods) => {
    const adyenPaymentInstrument = paymentMethods?.applicablePaymentMethods?.find(
        ({ id }) => id === 'AdyenComponent'
    )
    const adyenPaymentMethods = adyenPaymentInstrument?.c_adyenPaymentMethods
    const applePayPaymentMethod = adyenPaymentMethods?.paymentMethods?.find(
        ({ type }) => type === 'applepay'
    )
    return applePayPaymentMethod
}

const getEstimatedBasketTotal = (basket) => {
    const discountedMerchandiseTotal = Number(basket?.productTotal ?? basket?.productSubTotal) || 0
    const estimatedShipping = Number(basket?.shippingTotal) || 0
    const estimatedTax = basket?.taxTotal != null ? Number(basket.taxTotal) : 0
    const estimatedTotalValue =
        basket?.orderTotal > 0
            ? Number(basket.orderTotal)
            : (Number(basket?.productTotal) || discountedMerchandiseTotal) + estimatedShipping + estimatedTax
    return estimatedTotalValue
}

const getApplePayShippingMethodAmount = (shippingMethod, basket) => {
    const selectedShippingMethodId = basket?.shipments?.[0]?.shippingMethod?.id
    const selectedShipmentTotal = basket?.shipments?.[0]?.shippingTotal
    const adjustedShippingTotal = basket?.shippingTotal ?? selectedShipmentTotal
    const isSelectedMethod = shippingMethod?.id === selectedShippingMethodId

    if (isSelectedMethod && Number(adjustedShippingTotal) === 0) {
        return '0.00'
    }

    return (Number(shippingMethod?.price) || 0).toFixed(2)
}

const getApplePayContactField = (data, field) =>
    data?.authorizedEvent?.payment?.shippingContact?.[field] ||
    data?.authorizedEvent?.payment?.billingContact?.[field]

const ApplePayExpressButton = ({ setErrorMessage, isPdpApplePayExpress = false, pdpApplePayExpressProductData = null }) => {
    const intl = useIntl()
    const [isApplePayAvailable, setIsApplePayAvailable] = useState(false)
    const [paymentMethods, setPaymentMethods] = useState()
    const [shippingMethods, setShippingMethods] = useState()
    const applePayContainerRef = useRef(null) // the container div for the Apple Pay button
    const applePayComponentRef = useRef(null)
    const createOrderResponseRef = useRef(null) // to save the new order created in SFCC after Apple Pay authorization in onSubmit
    const [temporaryBasket, setTemporaryBasket] = useState()
    let { data: normalBasket, refetch: refetchNormalBasket } = useCurrentBasket()
    let basket = isPdpApplePayExpress ? temporaryBasket : normalBasket
    const {brandId} = useBrand()
    const { data: customer } = useCurrentCustomer()
    const { isRegistered } = customer || {}

    const { app: appConfig } = getConfig() //get ssr config

    const { locale, site, buildUrl } = useMultiSite()
    const deviceType = usedDeviceDetection()
    const { getTokenWhenReady } = useAccessToken()

    const parameters = {
        ...appConfig.commerceAPI.parameters,
        siteId: site?.id
    }
    const proxyPath = appConfig.commerceAPI.proxyPath
    const organizationId = parameters.organizationId
    const siteId = site?.id
    const localeId = locale?.id

    const getShopperBasketsV2Client = async () => {
        try {
            const token = await getTokenWhenReady()
    
            return new ShopperBasketsV2({
                proxy: `${window.location.origin}${proxyPath}`,
                parameters,
                headers: {
                    authorization: `Bearer ${token}`
                }
            })
        } catch (error) {
            console.error('Failed to get ShopperBasketsV2 client:', error)
            return null
        }
    }

    const getV2Parameters = (requestParameters = {}) => ({
        organizationId,
        siteId,
        locale: localeId,
        ...requestParameters
    })

    const refetchBasket = async () => {
        if (isPdpApplePayExpress) {
            const shopperBasketsV2 = await getShopperBasketsV2Client()

            const updatedBasket = await shopperBasketsV2.getBasket({
                parameters: getV2Parameters({
                    basketId: basket.basketId
                })
            })

            setTemporaryBasket(updatedBasket)

            return updatedBasket
        }

        const refetched = await refetchNormalBasket()

        return refetched?.data?.baskets?.[0]
    }

    useEffect(() => {
        if (!isPdpApplePayExpress) {
            return
        }

        const getOrCreateTemporaryBasket = async () => {
            try {
                const shopperBasketsV2 = await getShopperBasketsV2Client()
                let newOrExistingTemporaryBasket = null

                const storedTemporaryBasketId = sessionStorage.getItem('pdpApplePayTemporaryBasketId')
                console.log('storedTemporaryBasketId',storedTemporaryBasketId)
                if (storedTemporaryBasketId) {
                    // fetch the existing temporary basket and remove all items and payment instruments from it
                    newOrExistingTemporaryBasket = await shopperBasketsV2.getBasket({
                            parameters: getV2Parameters({
                                basketId: storedTemporaryBasketId
                            })
                        })
                    console.log('get exisitng temporary basket',newOrExistingTemporaryBasket)
                }
                if(newOrExistingTemporaryBasket && !(newOrExistingTemporaryBasket?.basketId == 'undefined' || newOrExistingTemporaryBasket?.type?.includes('basket-not-found')) ){
                    for (const productItem of newOrExistingTemporaryBasket.productItems || []) {
                        await shopperBasketsV2.removeItemFromBasket({
                                parameters: getV2Parameters({
                                    basketId: newOrExistingTemporaryBasket.basketId,
                                    itemId: productItem.itemId
                                })
                            })
                    }

                    for (const paymentInstrument of newOrExistingTemporaryBasket.paymentInstruments || []) {
                        await shopperBasketsV2.removePaymentInstrumentFromBasket({
                                parameters: getV2Parameters({
                                    basketId: newOrExistingTemporaryBasket.basketId,
                                    paymentInstrumentId: paymentInstrument.paymentInstrumentId
                                })
                            })
                    }
                } else {
                    // create a new temporary basket and add the product to it
                    do {
                        newOrExistingTemporaryBasket = await shopperBasketsV2.createBasket({
                            parameters: getV2Parameters({
                                temporary: true
                            }),
                            body: {}
                        })
                        console.log('create new temp basket', newOrExistingTemporaryBasket)
                        if((newOrExistingTemporaryBasket?.type?.includes('customer-temporary-baskets-quota-exceeded'))){
                            newOrExistingTemporaryBasket?.basketIds?.split(',').forEach(async (basketId) => {
                                await shopperBasketsV2.deleteBasket({
                                    parameters: getV2Parameters({
                                        basketId
                                    })
                                })
                            })
                        }
                            
                    } while (newOrExistingTemporaryBasket?.type?.includes('customer-temporary-baskets-quota-exceeded'))
                }
                sessionStorage.setItem('pdpApplePayTemporaryBasketId', newOrExistingTemporaryBasket.basketId)
                setTemporaryBasket(newOrExistingTemporaryBasket)
                await shopperBasketsV2.addItemToBasket({
                        parameters: getV2Parameters({
                            basketId: newOrExistingTemporaryBasket.basketId
                        }),
                        body: [
                            {
                                productId: pdpApplePayExpressProductData.pid,
                                quantity: pdpApplePayExpressProductData.qty
                            }
                        ]
                    })
            } catch (error) {
                sessionStorage.removeItem('pdpApplePayTemporaryBasketId')
                setTemporaryBasket(null)
                console.error('Failed to get or create PDP Apple Pay temporary basket:', error)
            }
        }

        getOrCreateTemporaryBasket()
    }, [isPdpApplePayExpress, pdpApplePayExpressProductData?.pid, pdpApplePayExpressProductData?.qty])


    const refetchShippingMethods = async () => {
        const shopperBasketsV2 = await getShopperBasketsV2Client()
        const updatedShippingMethods = await shopperBasketsV2.getShippingMethodsForShipment({
            parameters: getV2Parameters({
                basketId: basket.basketId,
                shipmentId: basket.shipments[0].shipmentId
            })
        })

        setShippingMethods(updatedShippingMethods)
        return {data: updatedShippingMethods}
    }

    const updateShippingMethodForShipment = async ({parameters: requestParameters, body}) => {
        const shopperBasketsV2 = await getShopperBasketsV2Client()
        const updatedBasket = await shopperBasketsV2.updateShippingMethodForShipment({
            parameters: getV2Parameters(requestParameters),
            body
        })
        return updatedBasket
    }

    const updateShippingAddressForShipment = async ({parameters: requestParameters, body}) => {
        const shopperBasketsV2 = await getShopperBasketsV2Client()
        const updatedBasket = await shopperBasketsV2.updateShippingAddressForShipment({
            parameters: getV2Parameters(requestParameters),
            body
        })
        return updatedBasket
    }

    const updateBillingAddressForBasket = async ({parameters: requestParameters, body}) => {
        const shopperBasketsV2 = await getShopperBasketsV2Client()
        const updatedBasket = await shopperBasketsV2.updateBillingAddressForBasket({
            parameters: getV2Parameters(requestParameters),
            body
        })
        return updatedBasket
    }

    const addPaymentInstrumentToBasket = async ({parameters: requestParameters, body}) => {
        const shopperBasketsV2 = await getShopperBasketsV2Client()
        const updatedBasket = await shopperBasketsV2.addPaymentInstrumentToBasket({
            parameters: getV2Parameters(requestParameters),
            body
        })
        return updatedBasket
    }

    const removePaymentInstrumentFromBasket = async ({parameters: requestParameters}) => {
        const shopperBasketsV2 = await getShopperBasketsV2Client()
        const updatedBasket = await shopperBasketsV2.removePaymentInstrumentFromBasket({
            parameters: getV2Parameters(requestParameters)
        })
        return updatedBasket
    }

    const updateCustomerForBasket = async ({parameters: requestParameters, body}) => {
        const shopperBasketsV2 = await getShopperBasketsV2Client()
        const updatedBasket = await shopperBasketsV2.updateCustomerForBasket({
            parameters: getV2Parameters(requestParameters),
            body
        })
        return updatedBasket
    }

    const updateBasket = async ({parameters: requestParameters, body}) => {
        const shopperBasketsV2 = await getShopperBasketsV2Client()
        const updatedBasket = await shopperBasketsV2.updateBasket({
            parameters: getV2Parameters(requestParameters),
            body
        })
        return updatedBasket
    }

    const shipmentId = basket?.shipments?.[0]?.shipmentId

    useEffect(() => {
        let cancelled = false

        const loadBasketData = async () => {
            if (!basket?.basketId || !shipmentId || !organizationId || !siteId) {
                return
            }

            try {
                const shopperBasketsV2 = await getShopperBasketsV2Client()
                const [paymentMethodsResponse, shippingMethodsResponse] = await Promise.all([
                    shopperBasketsV2.getPaymentMethodsForBasket({
                        parameters: getV2Parameters({
                            basketId: basket.basketId,
                            // The custom Adyen hook otherwise looks up the regular basket.
                            ...(isPdpApplePayExpress ? {c_expressBasketId: basket.basketId} : {})
                        })
                    }),
                    shopperBasketsV2.getShippingMethodsForShipment({
                        parameters: getV2Parameters({
                            basketId: basket.basketId,
                            shipmentId
                        })
                    })
                ])

                if (!cancelled) {
                    setPaymentMethods(paymentMethodsResponse)
                    setShippingMethods(shippingMethodsResponse)
                }
            } catch (error) {
                if (!cancelled) {
                    console.error('Failed to load Shopper Baskets V2 data:', error)
                }
            }
        }

        loadBasketData()

        return () => {
            cancelled = true
        }
    }, [basket?.basketId, shipmentId, organizationId, siteId, localeId])

    const applePayPaymentMethod = getApplePayPaymentMethod(paymentMethods)

    const CONTACT_US_TEL = getBrandLocaleEnvVarValue(appConfig, 'CONTACT_US_TEL', site?.id, locale?.id)
    const showGenericPaymentError = () => {
        setErrorMessage(
            intl.formatMessage({
                id: 'cart.message.generic_banner_error',
                defaultMessage:
                    'There’s a problem with your payment. Please try again or try a different payment method. If the problem persists, contact our customer service at {phoneNumber}.'
            }, { phoneNumber: CONTACT_US_TEL })
        )
    }

    const isUserCancelledApplePay = (errorOrResult) => {
        const name = errorOrResult?.name || ''
        const message = errorOrResult?.message || ''

        return (
            name.includes('CANCEL') ||
            message.includes('cancel') ||
            message.includes('Window is closed')
        )
    }

    const applePayValidationData = applePayPaymentMethod?.configuration // Apple merchant config values in Adyen
    const applePayDomain = getSiteEnvVarValue(appConfig, 'ADYEN_APPLEPAY_DOMAIN', site?.id)
    const applePayDomainBrand = getBrandSiteEnvVarValue(appConfig, applePayDomain, site?.id)
    const basketId = basket?.basketId
    const basketCurrency = basket?.currency
    const basketAmountValue = Math.round(getEstimatedBasketTotal(basket) * 100)
    const shippingMethodCount = shippingMethods?.applicableShippingMethods?.length || 0
    const applePayPaymentMethodType = applePayPaymentMethod?.type
    const applePayMerchantName = applePayPaymentMethod?.configuration?.merchantName
    
    // update the SFCC order's Payment Instrument with the result recieved from Adyen authorization in onSubmit
    const updatePaymentInstrument = useShopperOrdersMutation(ShopperOrdersMutations.UpdatePaymentInstrumentForOrder)
    const updateOrderPaymentInstrument = async (adyenPaymentResult) => {
        const createOrderResponse = createOrderResponseRef.current

        const adyenInstrument = createOrderResponse.paymentInstruments.find(
            (paymentInstrument) => paymentInstrument.paymentMethodId === 'AdyenComponent')

        const updatedOrderWithPaymentIntrument = await updatePaymentInstrument.mutateAsync({
            parameters: {
                orderNo: createOrderResponse.orderNo,
                paymentInstrumentId: adyenInstrument.paymentInstrumentId
            },
            body: {
                amount: adyenPaymentResult?.amount?.value
                    ? adyenPaymentResult.amount.value / 100
                    : undefined,
                paymentMethodId: 'AdyenComponent',
                c_adyenPaymentData: JSON.stringify(adyenPaymentResult)
            }
        })

    }


    // set fingerPrint required for Adyen authorization of applepay onSubmit
    const [fingerprintScriptLoaded, setFingerprintScriptLoaded] = useState(false)
    useEffect(() => {
        if (!document.getElementById('adyen-device-fingerprint')) {
            const input = document.createElement('input')
            input.type = 'hidden'
            input.id = 'adyen-device-fingerprint'
            input.name = 'adyen-device-fingerprint'
            document.body.appendChild(input)
        }

        if (!fingerprintScriptLoaded) {
            const script = document.createElement('script')

            const now = new Date()
            const yyyy = now.getFullYear()
            const mm = String(now.getMonth() + 1).padStart(2, '0')
            const dd = String(now.getDate()).padStart(2, '0')
            const version = `${yyyy}${mm}${dd}`

            script.src = `https://live.adyen.com/hpp/js/df.js?v=${version}`
            script.async = true
            script.nonce = window.__CSP_NONCE__

            script.onload = () => {
                setFingerprintScriptLoaded(true)
                window.dfSet('adyen-device-fingerprint')
            }

            document.body.appendChild(script)
        }
    }, [fingerprintScriptLoaded])

    // initialize the Adyen Checkout instance and mount the Apple Pay component in the container div
    useEffect(() => {
        const initAdyenCheckoutAndLoadApplePayInContainer = async () => {
            const countryCode = locale.id.split('-')[1]

            const adyenAmountObj = {
                value: Math.round(getEstimatedBasketTotal(basket) * 100),
                currency: basket.currency
            }
            const adyenCheckoutObj = await AdyenCheckout({
                clientKey: getSiteEnvVarValue(appConfig, 'ADYEN_CLIENT_KEY', site?.id),
                environment: getSiteEnvVarValue(appConfig, 'ADYEN_MODE', site?.id),
                locale: locale.id,
                countryCode,
                amount: adyenAmountObj,
                paymentMethodsResponse: {
                    paymentMethods: [applePayPaymentMethod]
                },
                onSubmit: async (state, component, actions) => {

                    // Keep the loyalty points instrument paired with any applied points coupon.
                    const paymentInstrumentsToRemove = (basket.paymentInstruments || []).filter(
                        (paymentInstrument) => paymentInstrument.paymentMethodId !== 'LOYALTY_POINTS'
                    )
                    for (const paymentInstrument of paymentInstrumentsToRemove) {
                        await removePaymentInstrumentFromBasket({
                            parameters: {
                                basketId: basket.basketId,
                                paymentInstrumentId: paymentInstrument.paymentInstrumentId
                            }
                        })
                    }
                    const signifydParameters = {
                        paymentMethod: state.data.paymentMethod
                    }

                    const signifydJsonString = JSON.stringify(signifydParameters)

                    const updatedBasket = await addPaymentInstrumentToBasket({
                        parameters: {
                            basketId: basket.basketId
                        },
                        body: {
                            paymentMethodId: 'AdyenComponent',
                            amount: getEstimatedBasketTotal(basket),
                            c_signifydPaymentData: signifydJsonString
                        }
                    })

                    const token = await getTokenWhenReady()
                    
                    // add brand to the basket
                    const updatedBasketWithExpressFlags = await updateBasket({
                        parameters: {basketId: basket.basketId},
                        body: {
                            c_isApplePayExpressOrder: true,
                            c_brandId: (brandId || 'garage').toLowerCase(),
                            c_isPdpApplePayExpressOrder: isPdpApplePayExpress
                        }
                    })


                    const createOrderRaw = await fetch(
                        `${proxyPath}/checkout/shopper-orders/v1/organizations/${organizationId}/orders?siteId=${siteId}&c_deviceType=${deviceType}`,
                        {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                Authorization: `Bearer ${token}`
                            },
                            body: JSON.stringify({
                                basketId: basket.basketId
                            })
                        }
                    )

                    const createOrderResponse = await createOrderRaw.json()
                    // Check Signifyd
                    if (createOrderResponse.c_SignifydPolicy == 'REJECT' || createOrderResponse.status == 'failed') {
                        const paymentInstruments = (
                            updatedBasket?.paymentInstruments || basket.paymentInstruments || []
                        ).filter((paymentInstrument) => paymentInstrument.paymentMethodId !== 'LOYALTY_POINTS')

                        for (const paymentInstrument of paymentInstruments) {
                            await removePaymentInstrumentFromBasket({
                                parameters: {
                                    basketId: basket.basketId,
                                    paymentInstrumentId: paymentInstrument.paymentInstrumentId
                                }
                            })
                        }
                        showGenericPaymentError()
                        actions.reject()
                        component?.setStatus?.('ready')
                        return
                    }
                    createOrderResponseRef.current = createOrderResponse
                    const paymentResult = await fetch(
                        `${proxyPath}/custom/adyen-payments/v1/organizations/${organizationId}/adyen-payments?siteId=${siteId}&c_brand=Garage`,
                        {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                Authorization: `Bearer ${token}`
                            },
                            body: JSON.stringify({
                                orderId: createOrderResponse.orderNo,
                                orderToken: createOrderResponse.orderToken,
                                adyenData: state,
                                deviceFingerprint: document.getElementById('adyen-device-fingerprint').value,
                                locale,
                                returnUrl: `${window.location.origin}/adyen?orderNo=${createOrderResponse.orderNo}`
                            })
                        }
                    )

                    const adyenPaymentResult = await paymentResult.json()

                    await updateOrderPaymentInstrument(adyenPaymentResult)
                    
                    if (!adyenPaymentResult.resultCode || adyenPaymentResult.resultCode === 'Error') {
                        actions.reject() // will call onPaymentFailed
                        return
                    }

                    actions.resolve(adyenPaymentResult) // will call onPaymentCompleted
                },
                onPaymentCompleted: async (result, component) => {

                    const createOrderResponse = createOrderResponseRef.current

                    
                    if (result?.resultCode?.toLowerCase() === 'authorised') {
                        window.location.href = buildUrl(`/checkout/confirmation/${createOrderResponse?.orderNo || ''}`)
                    }
                },

                onPaymentFailed: (result, component) => {
                    console.log('ApplePay onPaymentFailed result:', result, 'component:', component)
                    showGenericPaymentError()
                    component?.setStatus?.('ready')
                    applePayComponentRef.current?.unmount()
                    applePayComponentRef.current = null
                    initAdyenCheckoutAndLoadApplePayInContainer()
                },

                onError: (error, component) => {
                    console.log('ApplePay onError error:', error, 'component:', component)
                      if (!isUserCancelledApplePay(error)) {
                          showGenericPaymentError()
                      }
                    component?.setStatus?.('ready')
                    applePayComponentRef.current?.unmount()
                    applePayComponentRef.current = null
                    initAdyenCheckoutAndLoadApplePayInContainer()
                }
            })
            //create ApplePay component/HTML and mount it to the container
            try {
                applePayComponentRef.current = new ApplePay(adyenCheckoutObj, {
                    isExpress: true,
                    expressPage: 'cart',
                    buttonColor: 'white-outline',
                    buttonType: 'plain',
                    amount: adyenAmountObj,
                    countryCode,
                    requiredBillingContactFields: ['postalAddress', 'name'],
                    requiredShippingContactFields: [
                        'postalAddress',
                        'name',
                        'phone',
                        'email'
                    ],
                    onClick: async (resolve, reject) => {
                        try {
                            const updatedBasket = await refetchBasket() || basket
                            if (updatedBasket.productTotal == 0) {
                                
                                (pdpApplePayExpressProductData.variationAttributes || []).forEach((attr) => {
                                    if (!attr.selectedValue?.value) {
                                        throw new Error(intl.formatMessage(
                                            {
                                                defaultMessage: 'Please select a {attribute}',
                                                id: 'product_view.error.missing_single_attribute'
                                            },
                                            { attribute: attr.name.toLowerCase() }
                                        ))
                                    }
                                })
                            }
                            resolve()
                        } catch (error) {
                            console.error('ApplePay validation error:', error)
                            setErrorMessage(error?.message)
                            reject(error?.message)
                        }
                    },
                    onValidateMerchant: async (resolve, reject, validationURL) => {
                        try {
                            //send gtm event
                            sendClickEvent(isPdpApplePayExpress ? 'applepay_cta_pdp': 'applepay_cta_cartpage')
                            sendBeginCheckoutEvent(basket, {
                                checkout_entry_method: 'apple_pay',
                                checkout_location: isPdpApplePayExpress ? 'pdp' :'cart_page',
                                checkout_type: 'express',
                                payment_type: 'apple_pay'
                            })
                            
                            const token = await getTokenWhenReady()

                            const response = await fetch(
                                `${proxyPath}/custom/applepay-validation/v1/organizations/${organizationId}/applepay-validation?siteId=${siteId}`,
                                {
                                    method: 'POST',
                                    headers: {
                                        'Content-Type': 'application/json',
                                        Authorization: `Bearer ${token}`
                                    },
                                    body: JSON.stringify({
                                        merchantIdentifier: applePayValidationData.merchantId,
                                        displayName: applePayValidationData.merchantName,
                                        domainName: applePayDomainBrand || applePayDomain,
                                        validationURL
                                    })
                                }
                            )

                            const responseJson = await response.json()
                            resolve(responseJson)
                        } catch (error) {
                            console.error('ApplePay validation error:', error)
                            reject(error)
                        }
                    },

                    onShippingContactSelected: async (resolve, reject, event) => {
                        try {
                            const ApplePayErrorCtor = window.ApplePayError
                            const shippingCountryCode =
                                event.shippingContact?.countryCode?.toUpperCase()

                            let update = {}
                            const postalCodePrefixesToRestrict = getSiteEnvVarValue(appConfig, 'POSTAL_CODE_PREFIXES_TO_RESTRICT', site?.id) //reject address if first 2 characters of postal code are in it
                            if (shippingCountryCode !== countryCode|| 
                                    postalCodePrefixesToRestrict?.split(',').includes(event.shippingContact?.postalCode.trim().toUpperCase().substring(0,2)) ) {
                                const shippingCountryError = ApplePayErrorCtor
                                    ? new ApplePayErrorCtor(
                                        'shippingContactInvalid',
                                        'countryCode',
                                        'Cannot ship to the selected address'
                                    )
                                    : {
                                        code: 'shippingContactInvalid',
                                        contactField: 'countryCode',
                                        message: 'Cannot ship to the selected address'
                                    }

                                update = {
                                    // Get the total from the application state.
                                    newTotal: {
                                        label: applePayValidationData.merchantName,
                                        amount: getEstimatedBasketTotal(basket).toFixed(2),
                                        type: 'final'
                                    },
                                    errors: [shippingCountryError]
                                }
                                console.error('ApplePay onShippingContactSelected error: Cannot ship to the selected address. shippingCountryCode:', shippingCountryCode, 'postalCode:', event.shippingContact?.postalCode)
                                resolve(update)
                                return
                            }
                            basket = await updateShippingAddressForShipment({
                                parameters: {
                                    basketId: basket.basketId,
                                    shipmentId: basket.shipments[0].shipmentId
                                },
                                body: {
                                    city: event?.shippingContact?.locality,
                                    stateCode: event?.shippingContact?.administrativeArea,
                                    postalCode: event?.shippingContact?.postalCode?.trim().toUpperCase(),
                                    countryCode: event?.shippingContact?.countryCode
                                }
                            })

                            const updatedShippingMethods = await refetchShippingMethods() // refetch shipping methods after updating the shipping address in the basket
                            const shippingMethodsToSendToApplePay = updatedShippingMethods?.data?.applicableShippingMethods?.filter((shippingMethod) => {
                                return shippingMethod?.c_isAvailableForApplePayExpress
                            })?.map(
                                    (shippingMethod) => ({
                                        identifier: shippingMethod.id,
                                        label: shippingMethod.name,
                                        detail: shippingMethod.c_estimatedArrivalTime || '',
                                        amount: getApplePayShippingMethodAmount(shippingMethod, basket)
                                    })
                                ) || []
                            const updatedBasket = await refetchBasket() || basket
                            update = {
                                newShippingMethods: shippingMethodsToSendToApplePay,
                                newTotal: {
                                    label: applePayValidationData.merchantName,
                                    amount: getEstimatedBasketTotal(updatedBasket).toFixed(2),
                                    type: 'final'
                                }
                            }
                            resolve(update)
                        } catch (error) {
                            console.error('ApplePay onShippingContactSelected error:', error)
                            reject(error)
                        }
                    },

                    onShippingMethodSelected: async (resolve, reject, event) => {
                        try {
                            // Update the basket with shipping method returned from Apple Pay modal
                            basket = await updateShippingMethodForShipment({
                                parameters: {
                                    basketId: basket.basketId,
                                    shipmentId: basket.shipments[0].shipmentId
                                },
                                body: {
                                    id: event.shippingMethod.identifier
                                }
                            })
                            /// refetch the basket to get the updated shipping method and totals
                            const updatedBasket = await refetchBasket() || basket

                            // return success response to Adyen-Apple Pay
                            const update = {
                                newTotal: {
                                    label: applePayValidationData.merchantName,
                                    amount: getEstimatedBasketTotal(updatedBasket).toFixed(2),
                                    type: 'final'
                                }
                            }
                            resolve(update)
                        } catch (error) {
                            console.error('ApplePay onShippingMethodSelected error:', error)
                            reject(error)
                        }
                    },

                    onAuthorized: async (data, actions) => {
                        let applePayCheckoutSection = 'shippingContactInvalid'
                        try {
                            const {billingAddress, deliveryAddress} = data
                            const shippingPhone = getApplePayContactField(data, 'phoneNumber')
                            const billingPhone = billingAddress?.phone || shippingPhone
                            const customerEmail = isRegistered ? customer?.email : getApplePayContactField(data, 'emailAddress')
                            if (customerEmail) {
                                await updateCustomerForBasket({
                                    parameters: {basketId: basket.basketId},
                                    body: {email: customerEmail}
                                })
                            }
                            basket = await updateShippingAddressForShipment({
                                parameters: {
                                    basketId: basket.basketId,
                                    shipmentId: basket.shipments[0].shipmentId
                                },
                                body: {
                                    firstName: deliveryAddress.firstName,
                                    lastName: deliveryAddress.lastName,
                                    address1: deliveryAddress.street,
                                    city: deliveryAddress.city,
                                    stateCode: deliveryAddress.stateOrProvince,
                                    postalCode: deliveryAddress.postalCode,
                                    countryCode: deliveryAddress.country,
                                    phone: shippingPhone
                                }
                            })
                            applePayCheckoutSection = 'billingContactInvalid'
                            basket = await updateBillingAddressForBasket({
                                parameters: {
                                    basketId: basket.basketId
                                },
                                body: {
                                    firstName: billingAddress.firstName || deliveryAddress.firstName,
                                    lastName: billingAddress.lastName || deliveryAddress.lastName,
                                    address1: billingAddress.street,
                                    city: billingAddress.city,
                                    stateCode: billingAddress.stateOrProvince,
                                    postalCode: billingAddress.postalCode,
                                    countryCode: billingAddress.country,
                                    phone: billingPhone
                                }
                            })
                            actions.resolve() // will call onSubmit
                        } catch (error) {
                            const errorBody = await error?.response?.json()
                            console.error('Error in onAuthorized:', errorBody)
                            let errorMessage = error?.statusMessage || 'Invalid Contact Details';
                            let errorField = 'addressLines'
                            switch (errorBody?.statusCode) {
                                case 'PHONE_INVALID':
                                    errorField = 'phoneNumber'
                                    errorMessage = intl.formatMessage({
                                        defaultMessage: 'Please enter a valid phone number',
                                        id: 'use_address_fields.error.invalid_phone_format'
                                    })
                                    break
                                case 'EMAIL_INVALID':
                                    errorField = 'emailAddress'
                                    errorMessage = intl.formatMessage({
                                        defaultMessage: 'Please enter a valid email address',
                                        id: 'email.validation.error'
                                    })
                                    break
                            }
                            let applePayErrorObj = window.ApplePayError
                                ? new window.ApplePayError(
                                    applePayCheckoutSection,
                                    errorField,
                                    errorMessage
                                )
                                : {
                                    code: applePayCheckoutSection,
                                    contactField: errorField,
                                    message: errorMessage
                                }

                            console.error('ApplePay onAuthorized error:', error)
                            actions.reject(applePayErrorObj) // will call onPaymentFailed
                        }
                    }
                })
                await applePayComponentRef.current.isAvailable()
                setIsApplePayAvailable(true)
                applePayComponentRef.current.mount(applePayContainerRef.current)
            } catch (error) {
                setIsApplePayAvailable(false)
            }
        }
        if (basket && applePayPaymentMethod && shippingMethodCount > 0) {
            initAdyenCheckoutAndLoadApplePayInContainer()
        }
        //unmount the previous ApplePay button if exists
        return () => {
            applePayComponentRef.current?.unmount()
            applePayComponentRef.current = null
        }
    }, [
        basketId,
        basketCurrency,
        basketAmountValue,
        localeId,
        siteId,
        shipmentId,
        shippingMethodCount,
        applePayPaymentMethodType,
        applePayMerchantName,
    ])

    return (
        <div ref={applePayContainerRef} style={{display: isApplePayAvailable ? 'block' : 'none'}}/>
    )
}

export default ApplePayExpressButton
